import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang, LANGUAGES } from '../i18n/LangContext';

const Header = () => {
	const { t, lang, setLang } = useLang();
	const [isScrolled, setIsScrolled] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isLangOpen, setIsLangOpen] = useState(false);
	const langRef = useRef(null);

	const currentLang = LANGUAGES.find((l) => l.code === lang);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (langRef.current && !langRef.current.contains(e.target)) {
				setIsLangOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 20);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const navLinks = [
		{ name: t.footer.link_services, href: '/#services' },
		{ name: t.footer.link_about,    href: '/#about' },
		{ name: t.footer.link_contact,  href: '/#contact' },
	];

	return (
		<header
			className={`fixed w-full top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 shadow-sm py-4' : 'bg-transparent py-6'}`}
			style={{ overflow: 'visible' }}
		>
			<div className="container mx-auto px-6 flex justify-between items-center">
				<a href="/" className="text-2xl font-bold text-slate-900 tracking-tight">
					shopprograms<span className="text-blue-600">.com</span>
				</a>

				{/* Desktop Nav */}
				<nav className="hidden md:flex items-center space-x-8" style={{ overflow: 'visible' }}>
					{navLinks.map((link) => (
						<a
							key={link.name}
							href={link.href}
							className="text-slate-600 hover:text-blue-600 font-medium transition-colors"
						>
							{link.name}
						</a>
					))}
					{/* Language dropdown */}
					<div style={{ position: 'relative' }} ref={langRef}>
						<button
							onClick={() => setIsLangOpen((v) => !v)}
							className="lang-dropdown-trigger"
							aria-label="Select language"
							aria-expanded={isLangOpen}
						>
							<span className="lang-flag-lg">{currentLang?.flag}</span>
							<ChevronDown size={14} className={`lang-chevron${isLangOpen ? ' lang-chevron--open' : ''}`} />
						</button>
						<AnimatePresence>
							{isLangOpen && (
								<motion.ul
									initial={{ opacity: 0, y: -6, scale: 0.97 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									exit={{ opacity: 0, y: -6, scale: 0.97 }}
									transition={{ duration: 0.15 }}
									className="lang-dropdown"
									style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 9999 }}
								>
									{LANGUAGES.map((l) => (
										<li key={l.code}>
											<button
												onClick={() => { setLang(l.code); setIsLangOpen(false); }}
												className={`lang-dropdown-item${lang === l.code ? ' lang-dropdown-item--active' : ''}`}
												title={l.name}
											>
												<span className="lang-flag-lg">{l.flag}</span>
												<span className="lang-dropdown-name">{l.name}</span>
											</button>
										</li>
									))}
								</motion.ul>
							)}
						</AnimatePresence>
					</div>
				</nav>

				{/* Mobile Menu Button */}
				<button
					className="md:hidden text-slate-900"
					onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
				>
					{isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
				</button>
			</div>

			{/* Mobile Nav */}
			<AnimatePresence>
				{isMobileMenuOpen && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						className="md:hidden bg-white border-t border-slate-100 overflow-hidden"
					>
						<nav className="flex flex-col p-6 space-y-4">
							{navLinks.map((link) => (
								<a
									key={link.name}
									href={link.href}
									className="text-slate-600 hover:text-blue-600 font-medium text-lg"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									{link.name}
								</a>
							))}
							{/* Mobile language switcher */}
						<div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
							{LANGUAGES.map((l) => (
								<button
									key={l.code}
									onClick={() => { setLang(l.code); setIsMobileMenuOpen(false); }}
									className={`lang-dropdown-item${lang === l.code ? ' lang-dropdown-item--active' : ''}`}
									title={l.name}
								>
									<span className="lang-flag-lg">{l.flag}</span>
									<span className="lang-dropdown-name">{l.name}</span>
								</button>
							))}
						</div>
						</nav>
					</motion.div>
				)}
			</AnimatePresence>
		</header>
	);
};

export default Header;
